import { gql, useApolloClient } from '@apollo/client'

import { iamLogin } from 'src/api/iam/auth.hooks'
import type { ProfileResponse } from 'src/api/iam/profile.types'
import { FrgCompanySettingsPinColors } from 'src/hooks/useCompanyPinColors'
import { iamClient } from 'src/lib/api-client'
import { setUser } from 'src/reactive-variables'
import TokenStorage from 'src/utils/tokenStorage'

import { FrgUserForLink } from './user'

export const FrgActiveProject = gql`
  fragment FrgActiveProject on ProjectType {
    id
    name
    userAccessLevel
    logo
    company {
      id
      name
      dashboardLocation {
        id
        latitude
        longitude
        placeId
        name
      }
    }
    containerTypes {
      edges {
        node {
          id
          name
          length
          width
          height
          diameter
          isCylindrical
          volume
        }
      }
    }
    measurementsettingsSet {
      edges {
        node {
          id
        }
      }
    }
    wasteFractions {
      edges {
        node {
          id
          name
          wasteCategory
          wasteSubstance
          weightFactor
        }
      }
    }
  }
`

export const GET_ME = gql`
  ${FrgActiveProject}
  ${FrgUserForLink}
  ${FrgCompanySettingsPinColors}
  query GetMeByProjectIds($projectList: [ID]) {
    me {
      ...FrgUserForLink
      isStaff
      isAdmin
      isSuperuser
      isDemo
      isReseller
      isMaster
      isBeta
      userId
      email
      intercomUserHash
      createdAt
      phoneNumber
      logo
      datetimeFormatLocale
      activeProjectsIds
      daysBeforeDemoExpires
      language
      ownProjectsIds
      ownProjects {
        edges {
          node {
            id
            name
          }
        }
      }
      jobtitle {
        id
        title
      }
      activeProjects(projectsIds: $projectList) {
        edges {
          node {
            ...FrgActiveProject
          }
        }
      }
      company {
        id
        logo
        name
        isActive
        companySettings {
          id
          mapCountry
          metabaseEnabled
          ...FrgCompanySettingsPinColors
        }
        settings {
          clockFormat12
          currency
        }
        dashboardLocation {
          id
          latitude
          longitude
          placeId
          name
        }
        projectSet {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`

export const GET_ACTIVE_PROJECTS_DETAILS = gql`
  ${FrgActiveProject}
  query GetActiveProjectsDetails($ids: [ID]) {
    allProjects(ids: $ids) {
      edges {
        node {
          ...FrgActiveProject
        }
      }
    }
  }
`

type LoginFnArgs = {
  username: string
  password: string
  rememberMe: boolean
  onSuccess?: () => void
}

export const useLogin = () => {
  const client = useApolloClient()
  const login = async ({
    username,
    password,
    rememberMe,
    onSuccess,
  }: LoginFnArgs) => {
    await client.resetStore()
    const { access_token, refresh_token } = await iamLogin({
      username,
      password,
    })

    if (!access_token) {
      throw new Error('Authentication failed: IAM access token is missing')
    }

    TokenStorage.setRememberMe(rememberMe)
    TokenStorage.setIAMAccessToken(access_token)
    if (refresh_token) {
      TokenStorage.setIAMRefreshToken(refresh_token)
    }

    const { data: profile } = await iamClient.get<ProfileResponse>(
      '/users/me/profile'
    )

    const response = await client.query({ query: GET_ME })

    if (!response.data?.me) {
      throw new Error('Authentication failed: user data is missing')
    }

    setUser({
      ...response.data.me,
      id: profile.user_id,
      language: profile.language,
      timezone: profile.timezone,
      datetimeFormatLocale: profile.datetime_format_locale,
      firstName: profile.first_name,
      lastName: profile.last_name,
      email: profile.email,
      logo: profile.logo,
      color: profile.color,
    })

    onSuccess?.()
  }

  return { login }
}
